import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { adminSwrOptions } from "./adminSwr";

export interface StockOrdersBlockedOrganization {
  organizationId: number;
  /** null when the organization no longer resolves (deleted after being blocked). */
  name: string | null;
  navNumber: string | null;
}

export interface StockOrdersConfig {
  /** The GLOBAL kill-switch (blocks everyone). */
  stockOrdersDisabled: boolean;
  /** Per-customer block list, resolved server-side to name + NAV number. */
  blockedOrganizations: StockOrdersBlockedOrganization[];
}

export interface StockOrdersConfigRepository {
  useConfig(): Resource<StockOrdersConfig>;
  update(
    stockOrdersDisabled: boolean,
    blockedOrganizationIds: number[],
  ): Promise<void>;
}

const STOCK_ORDERS_CONFIG_KEY = "stock-orders-config";

export function useStockOrdersConfigRepository(): StockOrdersConfigRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useConfig(): Resource<StockOrdersConfig> {
    return useQuery(
      [STOCK_ORDERS_CONFIG_KEY],
      async (): Promise<StockOrdersConfig> => {
        const dto = await apiClient.getStockOrdersConfig();
        return {
          stockOrdersDisabled: dto.stockOrdersDisabled ?? false,
          blockedOrganizations: (dto.blockedOrganizations ?? []).map((o) => ({
            organizationId: o.organizationId,
            name: o.name ?? null,
            navNumber: o.navNumber ?? null,
          })),
        };
      },
      {
        // Admin surface — always show the current server state on entry.
        staleTime: 0,
        // Admin perf FE-3 (flag): paint from cache + one background refetch.
        ...adminSwrOptions(),
      },
    );
  }

  async function update(
    stockOrdersDisabled: boolean,
    blockedOrganizationIds: number[],
  ): Promise<void> {
    await apiClient.updateStockOrdersConfig(
      stockOrdersDisabled,
      blockedOrganizationIds,
    );
    await queryClient.invalidateQueries({
      queryKey: [STOCK_ORDERS_CONFIG_KEY],
    });
    // The ordering screens read stockOrdersEffectiveDisabled from
    // logos/options/all, so the admin's own session must refetch it to see
    // the change take effect without a full reload.
    await queryClient.invalidateQueries({ queryKey: ["logo-options"] });
  }

  return {
    useConfig,
    update,
  };
}
